import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Polls the client's pending advisor change-requests so the UI can surface a
// live "needs approval" badge. Refetches on a timer, on window focus, and
// whenever something dispatches `pfa:approvals-changed` (e.g. after the client
// approves/rejects, or an advisor queues a new request in the same tab).
export function usePendingApprovals(enabled) {
  const [count, setCount] = useState(0);
  const prev = useRef(0);
  const seeded = useRef(false);

  const refetch = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get("/connections/requests?status=pending");
      const next = (data.requests || []).length;
      setCount(next);
      // Notify only on a genuine increase after the first load.
      if (seeded.current && next > prev.current) {
        toast.info(
          next - prev.current === 1
            ? "A Personal Finance Advisor proposed a change for your approval"
            : `${next - prev.current} new advisor change requests to review`,
        );
      }
      prev.current = next;
      seeded.current = true;
    } catch (_) {
      /* ignore */
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    refetch();
    const id = setInterval(refetch, 25000);
    const onChange = () => refetch();
    window.addEventListener("pfa:approvals-changed", onChange);
    window.addEventListener("focus", onChange);
    return () => {
      clearInterval(id);
      window.removeEventListener("pfa:approvals-changed", onChange);
      window.removeEventListener("focus", onChange);
    };
  }, [enabled, refetch]);

  return { count, refetch };
}
